import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom event name for profile completion
export const PROFILE_COMPLETED_EVENT = 'profileCompleted';

/**
 * Dispatch a custom event when profile is marked as completed
 * Call this after updating localStorage with profile_completed = true
 */
export const dispatchProfileCompletedEvent = () => {
    window.dispatchEvent(new CustomEvent(PROFILE_COMPLETED_EVENT));
};

/**
 * Custom hook to redirect users who have already completed onboarding
 * Prevents users from accessing onboarding pages after profile completion
 *
 * Listens for:
 * 1. Initial mount check
 * 2. Storage events (changes in other tabs/windows)
 * 3. Custom profileCompleted event (dispatched after completion in same tab)
 */
export const useOnboardingRedirect = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const checkProfileCompletion = () => {
            const userDataString = localStorage.getItem('authUser');
            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);

                    // Check profile_completed flag (both camelCase and snake_case for compatibility)
                    const isProfileCompleted = userData.profile_completed || userData.profileCompleted;

                    if (isProfileCompleted) {
                        // Determine redirect based on user role
                        const userRole = userData.userType || userData.role;
                        const redirectTo = userRole === 'company' || userRole === 'recruiter'
                            ? '/companies/dashboard'
                            : '/candidate/dashboard';

                        navigate(redirectTo, { replace: true });
                    }
                } catch (error) {
                    console.error('[useOnboardingRedirect] Failed to parse user data:', error);
                }
            }
        };

        // Check immediately on mount
        checkProfileCompletion();

        // Listen for storage events (changes in other tabs/windows)
        window.addEventListener('storage', checkProfileCompletion);

        // Listen for custom profile completed event (same tab)
        window.addEventListener(PROFILE_COMPLETED_EVENT, checkProfileCompletion);

        return () => {
            window.removeEventListener('storage', checkProfileCompletion);
            window.removeEventListener(PROFILE_COMPLETED_EVENT, checkProfileCompletion);
        };
    }, [navigate]);
};
